import { createClient } from '@sanity/client'
import { safeImageUrl } from './sanity'
import {
  USE_CASE_NAV_QUERY,
  USE_CASE_PAGE_QUERY,
  type UseCaseNavLink,
  type UseCasePageData,
} from './useCaseQueries'
import { OG_IMAGE, SITE_NAME } from './seo'
import type { NavDropdownLink } from '../components/nav/NavDropdown'

function stripSiteSuffix(title: string) {
  return title.replace(/\s*\|\s*Induce.*$/i, '').trim()
}

export function normalizeCtaUrl(url: string): string {
  const trimmed = url.trim()
  if (!trimmed) return trimmed
  if (
    trimmed.startsWith('/') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('mailto:') ||
    trimmed.startsWith('#')
  ) {
    return trimmed
  }
  if (/^[a-z0-9.-]+\.[a-z]{2,}/i.test(trimmed)) {
    return `https://${trimmed}`
  }
  return trimmed
}

export function useCasePublicLabel(
  page: Pick<UseCasePageData, 'navLabel' | 'seoTitle' | 'slug'>,
): string {
  return page.navLabel?.trim() || stripSiteSuffix(page.seoTitle) || page.slug
}

export function mapUseCaseNavLink(row: UseCaseNavLink): NavDropdownLink | null {
  if (!row.slug) return null
  const label = row.navLabel?.trim() || stripSiteSuffix(row.seoTitle) || row.slug
  const description = row.navDescription?.trim() || row.seoDescription?.trim() || ''
  return {
    to: `/use-cases/${row.slug}`,
    label,
    description,
  }
}

export async function getUseCaseNavLinks(): Promise<NavDropdownLink[]> {
  const rows = await getUseCaseClient().fetch<UseCaseNavLink[]>(USE_CASE_NAV_QUERY)
  return rows.map(mapUseCaseNavLink).filter((link): link is NavDropdownLink => link != null)
}

function getUseCaseClient() {
  const projectId = import.meta.env.VITE_SANITY_PROJECT_ID
  if (!projectId) {
    throw new Error('Missing VITE_SANITY_PROJECT_ID - add it to .env (see .env.example)')
  }
  return createClient({
    projectId,
    dataset: import.meta.env.VITE_SANITY_DATASET ?? 'production',
    apiVersion: '2024-01-01',
    useCdn: true,
  })
}

export async function getUseCaseBySlug(slug: string): Promise<UseCasePageData | null> {
  const page = await getUseCaseClient().fetch<UseCasePageData | null>(USE_CASE_PAGE_QUERY, { slug })
  return page ? removeOutdatedAccessCopy(page) : null
}

// Published Sanity pages can retain copy from the former free beta campaign.
function removeOutdatedAccessCopy<T>(value: T): T {
  if (typeof value === 'string') {
    return value
      .replace(/Start free beta/gi, 'Choose a plan')
      .replace(/(?:Free beta|No credit card)\s*·\s*/gi, '')
      .replace(/\s*·\s*(?:Free beta|No credit card)/gi, '')
      .replace(/,?\s*including (?:the )?free beta/gi, '') as T
  }
  if (Array.isArray(value)) return value.map(removeOutdatedAccessCopy) as T
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, removeOutdatedAccessCopy(item)]),
    ) as T
  }
  return value
}

export async function getAllUseCaseSlugs(): Promise<string[]> {
  const rows = await getUseCaseClient().fetch<{ slug: string }[]>(
    `*[_type == "useCasePage" && !(_id in path("drafts.**")) && publishedAt <= now()]{ "slug": slug.current }`,
  )
  return rows.map((row) => row.slug).filter(Boolean)
}

export function resolveUseCaseSeoImage(page: UseCasePageData): string | undefined {
  return (
    safeImageUrl(page.ogImage, (b) => b.width(1200).height(630).format('jpg').quality(80)) ??
    safeImageUrl(page.hero?.media, (b) => b.width(1200).height(630).format('jpg').quality(80)) ??
    undefined
  )
}

export function resolveUseCaseHeroImageUrl(page: UseCasePageData): string | undefined {
  return (
    safeImageUrl(page.hero?.media, (b) => b.width(1280).auto('format')) ?? undefined
  )
}

export function useCasePageTitle(seoTitle: string) {
  return seoTitle.includes(SITE_NAME) ? seoTitle : `${seoTitle} | ${SITE_NAME}`
}

export function useCasePageMeta(page: UseCasePageData) {
  return {
    title: useCasePageTitle(page.seoTitle),
    description: page.seoDescription,
    path: `/use-cases/${page.slug}`,
    ogImage: resolveUseCaseSeoImage(page) ?? OG_IMAGE,
  }
}
